import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Skeleton } from '@vaiu/ui/skeleton';
import { i18n } from '@/utils/i18n';
import type { StorefrontGalleryImage } from '@/utils/merchantStorefrontGallery';

interface MerchantStorefrontPhotoGalleryProps {
  images: StorefrontGalleryImage[];
  businessName: string;
  loadingPhotos?: boolean;
}

const MerchantStorefrontPhotoGallery: React.FC<MerchantStorefrontPhotoGalleryProps> = ({
  images,
  businessName,
  loadingPhotos = false,
}) => {
  const sortedImages = useMemo(
    () => [...images].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [images]
  );

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const selectedImage =
    selectedIndex !== null ? sortedImages[selectedIndex] ?? null : null;

  const goPrev = () => {
    if (selectedIndex === null || sortedImages.length === 0) return;
    setSelectedIndex((selectedIndex - 1 + sortedImages.length) % sortedImages.length);
  };

  const goNext = () => {
    if (selectedIndex === null || sortedImages.length === 0) return;
    setSelectedIndex((selectedIndex + 1) % sortedImages.length);
  };

  if (!loadingPhotos && sortedImages.length === 0) {
    return null;
  }

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Images className="w-5 h-5" />
          {i18n.__('merchantPublicProfile.photosTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingPhotos && sortedImages.length === 0 ? (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full rounded-lg" />
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {i18n.__('merchantPublicProfile.loadingPhotos')}
            </p>
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2"
              role="list"
              aria-label={i18n.__('merchantPublicProfile.photosTitle')}
            >
              {sortedImages.map((image, index) => {
                const isSelected = selectedIndex === index;
                return (
                  <button
                    key={image.id}
                    type="button"
                    role="listitem"
                    aria-pressed={isSelected}
                    aria-label={
                      image.caption?.trim() ||
                      i18n.__('merchantPublicProfile.photoThumbnailAlt', {
                        name: businessName,
                        index: String(index + 1),
                      })
                    }
                    onClick={() => setSelectedIndex(index)}
                    className={`relative aspect-square overflow-hidden rounded-md border-2 bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      isSelected
                        ? 'border-blue-600 ring-2 ring-blue-200'
                        : 'border-transparent hover:border-muted-foreground/30'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.opacity = '0.3';
                      }}
                    />
                  </button>
                );
              })}
            </div>

            {selectedImage && (
              <div className="space-y-3 pt-2 border-t">
                <div className="relative flex items-center justify-center rounded-lg border bg-muted/30 p-2 min-h-[12rem]">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.caption || `${businessName} photo`}
                    className="max-h-[70vh] w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  {selectedImage.isPrimary && (
                    <Badge className="absolute top-3 left-3 bg-blue-600">
                      Primary
                    </Badge>
                  )}
                </div>

                {sortedImages.length > 1 && (
                  <div className="flex items-center justify-between gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={goPrev}>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {i18n.__('merchantPublicProfile.photoPrevious')}
                    </Button>
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {i18n.__('merchantPublicProfile.photoCounter', {
                        current: String((selectedIndex ?? 0) + 1),
                        total: String(sortedImages.length),
                      })}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={goNext}>
                      {i18n.__('merchantPublicProfile.photoNext')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}

                {selectedImage.caption?.trim() && (
                  <p className="text-sm text-muted-foreground">{selectedImage.caption}</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MerchantStorefrontPhotoGallery;
